<script setup lang="ts">
import { MapStateKey } from '@apps/main/modules/state/state';
import {
  useBookTripStore,
  useBrowserStore,
  usePriceCalendarStore,
  useCoreStore,
  useGeolocationStore,
  usePaxStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { useEditTripStore } from '@apps/main/new-desktop/core/stores/editTrip/editTripStore';
import { useDestinationSelectorStore } from '@apps/main/new-desktop/core/stores/destinationSelectorStore';

import { TransportType } from '@libs/trip-planner-interfaces';
import { injectStrict } from '@libs/utils';

import { PriceBreakdownModal } from '../../../modules/priceBreakdownModal';
import { EditTripModal } from '../../../modules/editTripModal';
import { DestinationSelectorModal } from '../../../modules/destinationSelector';
import { PaxSelectorModal } from '../../../modules/paxSelector';
import { FerryMultipleVehiclesModal } from '../../../modules/ferryMultipleVehiclesModal';
import { AirportSelectorModal } from '../../../modules/airportSelector';
import { WhyWeSuggestModal } from '../../../modules/suggestionTypeSelector';
import { PriceGuaranteeModal } from '../../../modules/priceGuarantee';
import { RemoveDestinationModal } from '../../../modules/removeDestinationModal';
import { MonthsSelectorModal } from '../../../modules/priceRibbon';
import { AddFlightsModal } from '../../../modules/addFlightsModal';
import type { AddFligthsModalRoute } from '../../../modules/addFlightsModal';
import { FerryDetailsModal } from '../../../modules/ferryDetailsModal';
import { FlightDetailsModal } from '../../../modules/flightDetailsModal';
import { ExternalErrorModal } from '../../../modules/externalErrorModal';
import type { PortSelectionUpdatePayload } from '../models';
import { TripOverviewModalType } from '../models';
import {
  usePremadeTripStore,
  useTripOverviewPageStore,
  useTripOverviewModalsStore,
  usePortSelectorStore,
  useTripOverviewFerryMultipleVehiclesStore,
} from '../stores';

import ContinueWithSelectedDateModal from './modals/ContinueWithSelectedDateModal.vue';
import PremadeTripDescriptionModal from './modals/PremadeTripDescriptionModal.vue';
import { PortSelectorModal } from './modals/portSelectorModal';
import BookingErrorModal from './BookingErrorModal.vue';

const bookTripStore = useBookTripStore();
const browserStore = useBrowserStore();
const coreStore = useCoreStore();
const destinationSelectorStore = useDestinationSelectorStore();
const editTripStore = useEditTripStore();
const geolocationStore = useGeolocationStore();
const mapState = injectStrict(MapStateKey);
const paxStore = usePaxStore();
const portSelectorStore = usePortSelectorStore();
const premadeTripStore = usePremadeTripStore();
const statsigEventsStore = useStatsigEventsStore();
const tripOverviewFerryMultipleVehiclesStore =
  useTripOverviewFerryMultipleVehiclesStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const priceCalendarStore = usePriceCalendarStore();

function onConfirmAddFlightsModal(routes: AddFligthsModalRoute[]): void {
  statsigEventsStore.logTripOverviewFlightAdded();
  tripOverviewPageStore.confirmAddFlightsModal(routes);
}

function onConfirmPortSelectionModal(
  payload: PortSelectionUpdatePayload
): void {
  tripOverviewPageStore.setUserSelectedTransport(true);
  if (portSelectorStore.transportType === TransportType.Flight) {
    statsigEventsStore.logTripOverviewFlightAdded();
  }
  if (portSelectorStore.transportType === TransportType.Ferry) {
    statsigEventsStore.logTripOverviewFerryAdded();
  }
  tripOverviewModalsStore.confirmPortSelectionModal(payload);
}

function onBuildANewTrip(): void {
  statsigEventsStore.logBookingFailedErrorBuildNewTrip();
  tripOverviewModalsStore.buildNewTrip();
}

function onTryAgain(): void {
  statsigEventsStore.logBookingFailedErrorTryAgain();
  tripOverviewModalsStore.closeTripOverviewModal();
}
</script>

<template>
  <AddFlightsModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
      TripOverviewModalType.AddFlights
    "
    :routes="tripOverviewPageStore.getAddFlightsModalRoutes()"
    :start-positon="mapState.startingLocation.value"
    :end-position="mapState.endLocation.value"
    @close="tripOverviewModalsStore.closeAddFlightsModal()"
    @confirm="onConfirmAddFlightsModal"
  />

  <AirportSelectorModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
        TripOverviewModalType.HomeAirportSelector &&
      tripOverviewPageStore.homeFlightData
    "
    :initial-airport="tripOverviewPageStore.homeFlightData.airport"
    :initial-user-position="geolocationStore.userGeolocation"
    @change-airport="
      tripOverviewModalsStore.confirmHomeAirportSelectorModal(
        tripOverviewPageStore.homeFlightData,
        $event
      )
    "
    @cancel-modal="tripOverviewModalsStore.closeTripOverviewModal"
  >
    <template #header-text>
      {{
        tripOverviewPageStore.homeFlightData.outboundIngredientId
          ? $t('planner.transport.fly_from')
          : $t('planner.transport.fly_back_to')
      }}
    </template>
  </AirportSelectorModal>

  <BookingErrorModal v-if="bookTripStore.bookingError" />

  <DestinationSelectorModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
      TripOverviewModalType.DestinationSelector
    "
    :search-text="destinationSelectorStore.searchText"
    :autocomplete-type="destinationSelectorStore.autocompleteType"
    @close-modal="tripOverviewModalsStore.closeTripOverviewModal"
    @select-my-position="tripOverviewModalsStore.selectUserPosition"
    @change-destination="
      tripOverviewModalsStore.confirmDestinationSelectorModal
    "
  />

  <FerryDetailsModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
        TripOverviewModalType.FerryDetails &&
      tripOverviewModalsStore.ferryDetailsModalData
    "
    :ferry-data="tripOverviewModalsStore.ferryDetailsModalData"
    :image-handler="coreStore.handleImageLoadError"
    :pax="paxStore.getPaxesButtonSimpleText"
    @close="tripOverviewModalsStore.closeFerryDetailsModal()"
  />
  <FlightDetailsModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
        TripOverviewModalType.FlightDetails &&
      tripOverviewModalsStore.flightDetailsModalData !== undefined
    "
    :flight-data="tripOverviewModalsStore.flightDetailsModalData"
    :image-handler="coreStore.handleImageLoadError"
    :pax="paxStore.getPaxesButtonSimpleText"
    @close="tripOverviewModalsStore.closeFlightDetailsModal"
  />

  <PaxSelectorModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
      TripOverviewModalType.PaxSelector
    "
    :initial-paxes="paxStore.initialPaxes"
    :should-show-tickets-warning="paxStore.shouldShowTicketsWarning"
    :should-show-room-selection-warning="
      paxStore.shouldShowRoomSelectionWarning
    "
    :should-show-number-of-vehicles-updated-warning="false"
    :should-show-number-of-vehicles-will-be-updated-warning="
      paxStore.shouldShowNumberOfVehiclesWillBeUpdatedWarning
    "
    @confirm-modal="tripOverviewModalsStore.confirmPaxSelectorModal"
    @cancel-modal="tripOverviewModalsStore.closePaxSelectorModal"
  />

  <PortSelectorModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
      TripOverviewModalType.PortSelector
    "
    :transport-type="portSelectorStore.transportType"
    :initial-from-port="portSelectorStore.initalFromPort"
    :initial-to-port="portSelectorStore.initalToPort"
    :ferry-suggestions="portSelectorStore.ferryRoutesSuggestions"
    :can-add-return-ingredient="portSelectorStore.canAddReturnIngredient"
    @close="tripOverviewPageStore.closePortSelectionModal"
    @confirm="onConfirmPortSelectionModal"
  />

  <FerryMultipleVehiclesModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
        TripOverviewModalType.FerryMultipleVehicles &&
      tripOverviewFerryMultipleVehiclesStore.data
    "
    :data="tripOverviewFerryMultipleVehiclesStore.data"
    @confirm="tripOverviewModalsStore.confirmFerryMultipleVehiclesModal"
    @cancel="tripOverviewModalsStore.closeFerryMultipleVehiclesModal"
  />

  <PriceGuaranteeModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
      TripOverviewModalType.PriceGuarantee
    "
    @close="tripOverviewModalsStore.closeTripOverviewModal"
  />

  <PremadeTripDescriptionModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
        TripOverviewModalType.PremadeTripDescription &&
      premadeTripStore.premadeTrip
    "
    :premade-trip="premadeTripStore.premadeTrip"
    @close="tripOverviewModalsStore.closeTripOverviewModal"
  />

  <RemoveDestinationModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
      TripOverviewModalType.RemoveDestination
    "
    @close="tripOverviewModalsStore.closeTripOverviewModal"
    @confirm="tripOverviewModalsStore.confirmRemoveDestinaionModal"
  />

  <WhyWeSuggestModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
      TripOverviewModalType.WhyWeSuggest
    "
    @close="tripOverviewModalsStore.closeTripOverviewModal"
  />

  <EditTripModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
      TripOverviewModalType.EditTrip
    "
    :is-desktop="!browserStore.isSmallerThanSmallMin"
    :segments="editTripStore.modalData"
    :has-bookable-transport="editTripStore.hasBookableTransport"
    :initial-step="editTripStore.initialStep"
    @close="tripOverviewModalsStore.closeEditTripModal"
    @change-trip-order="editTripStore.changeTripOrder"
    @add-destination="destinationSelectorStore.handleConfirmSelection"
    @add-destination-to-own-stay="
      destinationSelectorStore.handleOwnStayConfirmSelection(
        $event.suggestion,
        $event.ingredientId
      )
    "
    @change-destination="
      destinationSelectorStore.changeDestinationByIngredientId(
        $event.suggestion,
        $event.ingredientId
      )
    "
    @change-number-of-nights="editTripStore.changeNumberOfNights"
    @remove-destination="destinationSelectorStore.removeDestination"
  />

  <ContinueWithSelectedDateModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
      TripOverviewModalType.ContinueWithSuggestedDate
    "
    :trip-date-range="tripOverviewModalsStore.tripDateRange"
    :trip-number-of-nights="tripOverviewModalsStore.tripNumberOfNights"
    @confrim="tripOverviewModalsStore.confirmContinueWithSuggestedDateModal"
    @close="tripOverviewModalsStore.closeContinueWithSuggestedDateModal"
  />

  <PriceBreakdownModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
        TripOverviewModalType.PriceBreakdown &&
      tripOverviewModalsStore.stayPriceBreakdown
    "
    :data="tripOverviewModalsStore.stayPriceBreakdown"
    @close="tripOverviewModalsStore.closePriceBreakdownModal"
  />

  <MonthsSelectorModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
      TripOverviewModalType.MonthsSelector
    "
    :months="priceCalendarStore.priceRibbonMonths"
    :selected-month-iso-date="priceCalendarStore.selectedMonthOption.id"
    @confirm="tripOverviewModalsStore.confirmMonthsSelectorModal"
    @close="tripOverviewModalsStore.closeMonthsSelectorModal"
  />

  <ExternalErrorModal
    v-if="
      tripOverviewModalsStore.tripOverviewModalType ===
      TripOverviewModalType.ExternalError
    "
    @build-a-new-trip="onBuildANewTrip"
    @try-again="onTryAgain"
  />
</template>

<style scoped lang="scss">
.filters-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-16);

  .reset-filters-button {
    text-align: left;
  }
}
</style>
